import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sparkles, Users, TrendingUp, FileText, Settings, LogOut, Menu, X, Plus } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const BrokerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchBrokerData();
  }, []);

  const fetchBrokerData = async () => {
    try {
      const [statsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/broker/stats`),
        axios.get(`${API}/broker/clients`)
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data.slice(0, 5)); // Show only recent 5
    } catch (error) {
      toast.error('Failed to load broker data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-heading">Loading your dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-heading font-bold text-gradient">DISPUTEKING.AI</span>
              <span className="block text-xs text-slate-600">Broker Portal</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => navigate('/broker')} className="text-violet-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Clients</button>
              <button onClick={() => navigate('/education')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">Resources</button>
              <button onClick={() => navigate('/settings')} className="text-slate-600 hover:text-violet-600 font-medium transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <Button 
                data-testid="logout-button"
                onClick={handleLogout}
                variant="ghost"
                className="font-heading"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-4">
              <button onClick={() => navigate('/broker')} className="block w-full text-left text-violet-600 font-medium">Dashboard</button>
              <button onClick={() => navigate('/broker/clients')} className="block w-full text-left text-slate-600 font-medium">Clients</button>
              <button onClick={() => navigate('/education')} className="block w-full text-left text-slate-600 font-medium">Resources</button>
              <button onClick={() => navigate('/settings')} className="block w-full text-left text-slate-600 font-medium">Settings</button>
              <Button onClick={handleLogout} variant="ghost" className="w-full justify-start">Logout</Button>
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading font-bold mb-2">
            Welcome back, {user.broker_company || user.full_name}!
          </h1>
          <p className="text-slate-600">Here's your business overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <Users className="w-8 h-8 text-violet-600" />
              <span className="text-sm text-slate-600">Total Clients</span>
            </div>
            <div className="text-4xl font-heading font-bold text-gradient">{stats.total_clients}</div>
          </div>

          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-green-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className="w-8 h-8 text-green-600" />
              <span className="text-sm text-slate-600">Active</span>
            </div>
            <div className="text-4xl font-heading font-bold text-green-600">{stats.active_clients}</div>
          </div>

          <div className="bg-white rounded-3xl p-6 border-2 border-slate-100 hover:border-blue-200 hover:shadow-xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <FileText className="w-8 h-8 text-blue-600" />
              <span className="text-sm text-slate-600">Total Disputes</span>
            </div>
            <div className="text-4xl font-heading font-bold text-blue-600">{stats.total_disputes}</div>
          </div>

          <div className="bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-3xl p-6 text-white hover:shadow-2xl transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm">Pending</span>
            </div>
            <div className="text-4xl font-heading font-bold">{stats.pending_disputes}</div>
            <div className="text-sm text-violet-100 mt-2">Awaiting review</div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <button
            data-testid="add-client-button"
            onClick={() => navigate('/broker/clients')}
            className="bg-white rounded-3xl p-8 border-2 border-slate-100 hover:border-violet-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-violet-100 flex items-center justify-center text-violet-600 group-hover:scale-110 transition-transform">
                <Plus className="w-6 h-6" />
              </div>
              <span className="text-violet-600 font-heading font-semibold">Add New →</span>
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">Add Client</h3>
            <p className="text-slate-600">Onboard a new client to your CRM</p>
          </button>

          <button
            onClick={() => navigate('/broker/clients')}
            className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-3xl p-8 text-white hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 text-left group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="w-6 h-6" />
              </div>
              <span className="font-heading font-semibold">View All →</span>
            </div>
            <h3 className="text-xl font-heading font-semibold mb-2">Manage Clients</h3>
            <p className="text-green-100">View and update client information</p>
          </button>
        </div>

        {/* Recent Clients */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-heading font-bold">Recent Clients</h2>
            <Button
              onClick={() => navigate('/broker/clients')}
              variant="outline"
              className="rounded-full font-heading"
            >
              View All
            </Button>
          </div>
          {clients.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">No clients yet</h3>
              <p className="text-slate-600 mb-6">Start by adding your first client</p>
              <Button
                onClick={() => navigate('/broker/clients')}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full font-heading"
              >
                Add First Client
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {clients.map((client) => (
                <div
                  key={client.id}
                  onClick={() => navigate(`/broker/clients/${client.id}`)}
                  className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 hover:border-violet-200 hover:bg-violet-50 transition-all cursor-pointer"
                >
                  <div>
                    <h3 className="font-heading font-semibold">{client.client_name}</h3>
                    <p className="text-sm text-slate-600">{client.client_email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      client.status === 'active' ? 'bg-green-100 text-green-800' :
                      client.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {client.status}
                    </span>
                    <p className="text-sm text-slate-600 mt-1">{client.total_disputes} disputes</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Important Resources Section */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
            <h3 className="text-xl font-heading font-bold mb-3">🛡️ Report Identity Theft</h3>
            <p className="text-blue-50 mb-6">
              Help your clients report identity theft or fraudulent accounts to the FTC. 
              Essential for cases involving unauthorized accounts.
            </p>
            <a
              href="https://www.identitytheft.gov/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-blue-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-blue-50 transition-colors"
            >
              Report to FTC →
            </a>
          </div>

          <div className="bg-gradient-to-br from-violet-500 to-purple-500 rounded-3xl p-8 text-white hover:shadow-2xl transition-all">
            <h3 className="text-xl font-heading font-bold mb-3">📊 Credit Monitoring for Clients</h3>
            <p className="text-violet-50 mb-6">
              Recommend Identity IQ monitoring to your clients for real-time credit alerts and comprehensive tracking.
            </p>
            <a
              href="https://www.identityiq.com/sc-securepreferred.aspx?offercode=4312985V"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-violet-600 px-6 py-3 rounded-full font-heading font-semibold hover:bg-violet-50 transition-colors"
            >
              Join Identity IQ Monitoring →
            </a>
          </div>
        </div>

        <AIChatbot user={user} />
      </div>
    </div>
  );
};

export default BrokerDashboard;